import { Center } from "@chakra-ui/react";
import { withBucket } from "@gitbook/adaptive";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";

import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import { useEnvironmentUnsafe } from "@/common/contexts/EnvironmentContext";
import { getAllowTracking } from "@/common/utils/analytics/tracking";
import {
  API_URL,
  ENVIRONMENT,
  REFLAG_PUBLISHABLE_KEY,
} from "@/common/utils/env";
import { ReflagBootstrappedProvider, useClient } from "@reflag/react-sdk";

export function ReflagProvider({ children }: { children: React.ReactNode }) {
  const { user, bootstrapFlags } = useEnvironmentUnsafe() ?? {};

  // skip rendering the Reflag component if the tracking key or bootstrap flags are not set
  if (!REFLAG_PUBLISHABLE_KEY || !bootstrapFlags) {
    return children;
  }

  return (
    <ReflagBootstrappedProvider
      apiBaseUrl={`${API_URL}/ingest-proxy`}
      enableTracking={getAllowTracking()}
      flags={bootstrapFlags}
      initialLoading={true}
      loadingComponent={
        <Center h="100vh">
          <DelayedSpinner />
        </Center>
      }
      offline={ENVIRONMENT === "test" || ENVIRONMENT === "smoketest"}
      publishableKey={REFLAG_PUBLISHABLE_KEY}
      staleWhileRevalidate={false}
      timeoutMs={10000}
      toolbar={user?.isReflagAdmin}
    >
      <SentryIntegration />
      <GitBookIntegration />
      {children}
    </ReflagBootstrappedProvider>
  );
}

function SentryIntegration() {
  const client = useClient();
  useEffect(() => {
    return client?.on("check", (check) => {
      // Add feature flag evaluation to Sentry for error context
      const flagsIntegration =
        Sentry.getClient()?.getIntegrationByName<Sentry.FeatureFlagsIntegration>(
          "FeatureFlags",
        );
      if (flagsIntegration) {
        flagsIntegration.addFeatureFlag(check.key, check.value);
      }
    });
  }, [client]);
  return null;
}

function GitBookIntegration() {
  const client = useClient();
  useEffect(() => {
    if (!client) {
      return;
    }

    return withBucket(client);
  }, [client]);
  return null;
}
